<template>
  <div>
    <reports-title :title="pageTitle" />
    <reports-toolbar
      :drop-menu="menu"
      :range-value="toolbarRange"
      :report-title="pageTitle"
      :toolbar-disabled="isStatic"
      @onSelectDropdown="onChangeCalculationMode"
    />
    <skeleton-wrapper
      v-if="isShowSkeleton"
      id="skeletonWrapper"
    >
      <template #body>
        <line-chart
          id="report_export_id"
          :config="stubConfig"
          :is-static="true"
          :show-stub-message="false"
        />
      </template>
    </skeleton-wrapper>
    <line-chart
      v-else
      id="report_export_id"
      :config="chartConfig"
      :is-static="isStatic"
    />
  </div>
</template>

<script>
import reportsTitle from '../include/title.vue';
import reportsToolbar from '../include/toolbar.vue';
import lineChart from '../include/lineChart.vue';

import moment from '../../../libs/moment';
import constants from '../../../helpers/constants';
import moduleGanttDateWorker from '../../gantt/modules/ganttDateWorker';
import baselinesModel from '../../../models/baselines';
import Progress from './progress';
import charts from './chartsConfig';
import filterConstants from '../../filter/constants';
import reportsConstants from '../include/constants';
import skeletonWrapper from '../../common/VueComponents/skeletons/skeletonWrapper.vue';
import statusHelper from '../../../helpers/status';

const MONTH_LOCALES = [
  __('report_month_jan'),
  __('report_month_feb'),
  __('report_month_march'),
  __('report_month_apr'),
  __('report_month_may'),
  __('report_month_jun'),
  __('report_month_jul'),
  __('report_month_aug'),
  __('report_month_sep'),
  __('report_month_oct'),
  __('report_month_nov'),
  __('report_month_dec'),
];

export default {
  components: {
    reportsTitle, reportsToolbar, lineChart, skeletonWrapper,
  },
  data() {
    return {
      locale: __,
      projectId: null,
      baselineId: null,
      baselineData: {},
      baselines: [],
      chartConfig: charts.monthProgressChart().stub,
      stubConfig: charts.monthProgressChart().stub,
      clientWidth: 0,
      calculationMode: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
      toolbarRange: '',
      isStatic: true,
      isShowSkeleton: true,
    };
  },
  computed: {
    pageTitle() {
      return reportsConstants.TITLE.progress.monthProgress;
    },
    menu() {
      return [
        {
          type: 'item',
          name: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED}`),
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
          isDisabled: this.isStatic,
          tooltip: { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED}_single_report`), position: 'right' },
        },
        {
          type: 'submenu',
          name: constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE}`),
          list: this.baselines,
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE,
          isDisabled: !this.baselines.length || this.isStatic,
          selectedSubmenuId: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE ? this.baselineId : null,
          tooltip: !this.baselines.length ? { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE}_single_report`), position: 'right' } : null,
        },
        {
          type: 'submenu',
          name: constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE}`),
          list: this.baselines,
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE,
          isDisabled: !this.baselines.length || this.isStatic,
          selectedSubmenuId: this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE ? this.baselineId : null,
          tooltip: !this.baselines.length ? { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE}_single_report`), position: 'right' } : null,
        },
      ];
    },
    leftSidebarWidth() {
      return this.$store.getters.leftSidebarWidth;
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    baselineTasks() {
      if (this.baselineData?.tasks) {
        const tasksAndMilestones = this.baselineData.tasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

        return tasksAndMilestones;
      }

      return [];
    },
  },
  watch: {
    'currentFilter.properties.gantt_id': async function (projectsIds) {
      if (projectsIds?.length) {
        this.projectId = projectsIds[0];
      } else {
        this.projectId = null;
      }
    },
    'currentFilter.properties.rangeDate': function () {
      this.calcProgressReport();
      this.setToolbarRangeValue();
    },
    leftSidebarWidth(width) {
      this.clientWidth = `${document.documentElement.clientWidth - width}px`;
    },
    async projectId(id) {
      if (id) {
        await this.getBaselines();
        this.calcProgressReport();
      } else {
        setTimeout(() => {
          const stub = charts.monthProgressChart().stub;

          this.chartConfig = { ...stub };
          this.isStatic = true;
          this.updateFilterAdvancedSettings({ isShowNotificationMessage: false });
        }, 0);
      }
    },
  },
  async mounted() {
    const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

    userExtAnalytics.log('report_open', {
      report_type: reportNameAnalytics,
      calculation_mode: this.calculationMode,
    });

    this.init();
    this.clientWidth = `${document.documentElement.clientWidth - this.leftSidebarWidth}px`;
    this.setToolbarRangeValue();
  },
  methods: {
    init() {
      moduleGanttDateWorker.init();
      this.calculationMode = this.$route?.params?.calculationMode?.id || constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;

      if (this.currentFilter?.properties?.gantt_id?.length) {
        this.projectId = this.currentFilter.properties.gantt_id[0];
      }
    },

    setToolbarRangeValue() {
      const range = this.currentFilter?.properties?.rangeDate;

      if (range?.start && range?.end) {
        const start = moment(range.start);
        const end = moment(range.end);

        this.toolbarRange = `${start.date()} ${MONTH_LOCALES[start.month()]} ${start.year()} 
          - ${end.date()} ${MONTH_LOCALES[end.month()]} ${end.year()}`;
      } else {
        this.toolbarRange = '';
      }
    },

    updateFilterAdvancedSettings(settings) {
      this.$store.commit('filter/updateAdvancedSettingsByFilterType', {
        type: this.currentFilter.type,
        settings,
      });
    },

    async getBaselines() {
      await baselinesModel.init(this.projectId);
      const baselines = baselinesModel.getByGanttId(this.projectId)
        .map(item => ({ ...item, created_at: new Date(item.created_at) }))
        .sort((a, b) => a.created_at - b.created_at);

      if (baselines.length) {
        this.baselines = baselines;
        this.baselineId = baselines[baselines.length - 1].id;
        await this.getBaselineData(this.baselineId);
        this.updateFilterAdvancedSettings({ isShowNotificationMessage: false });
      } else {
        this.baselines = null;
        this.baselines = [];

        const calculationswithBaseline = [constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE, constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE];

        if (calculationswithBaseline.includes(this.calculationMode)) {
          this.updateFilterAdvancedSettings({ isShowNotificationMessage: true });
        }
        this.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
      }
    },
    async getBaselineData(id) {
      const baseline = this.baselines.find(b => b.id === id);

      if (!baseline) {
        this.baselineData = {};

        return;
      }

      const data = await baselinesModel.getByGanttIdAndEventId(baseline.gantt_id, baseline.event_id);

      if (data) {
        this.baselineData = data;
        this.baselineId = id;
      } else {
        this.baselineData = {};
      }
    },
    async calcProgressReport() {
      const result = await Progress.calculateMonthProgressReport({
        projectId: this.projectId,
        calculationMode: this.calculationMode,
        baselineTasks: this.baselineTasks,
      });

      if (result) {
        this.chartConfig = { ...result };
        this.isStatic = false;
      } else {
        setTimeout(() => {
          const stub = charts.monthProgressChart().stub;

          this.chartConfig = { ...stub };
          this.isStatic = true;
        }, 0);
      }
      this.isShowSkeleton = false;
      statusHelper.handlers.hideMainSpinner();
    },
    async onChangeCalculationMode(selected) {
      statusHelper.handlers.showMainSpinner();
      const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

      userExtAnalytics.log('report_calculation_mode_change', {
        report_type: reportNameAnalytics,
        from: this.calculationMode,
        to: selected.type,
      });

      this.calculationMode = selected.type;
      let baselineId = selected.baselineId;

      if (selected.type === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
        baselineId = this.baselines[this.baselines.length - 1].id;
      }

      if (baselineId) {
        await this.getBaselineData(baselineId);
      } else {
        this.baselineId = null;
      }

      this.calcProgressReport();
    },
  },
};
</script>
